// Odds and ends

import { Tinode } from 'tinode-sdk';

// Make shortcut icon appear with a green dot + show unread count in title.
export function updateFavicon(count) {
  const oldIcon = document.getElementById('shortcut-icon');
  const head = document.head || document.getElementsByTagName('head')[0];
  const newIcon = document.createElement('link');
  newIcon.type = 'image/png';
  newIcon.id = 'shortcut-icon';
  newIcon.rel = 'shortcut icon';
  newIcon.href = 'img/logo32x32' + (count > 0 ? 'a' : '') + '.png';
  if (oldIcon) {
    head.removeChild(oldIcon);
  }
  head.appendChild(newIcon);

  document.title = (count > 0 ? '('+count+') ' : '') + 'Tinode';
}

// Create theCard which represents user's or topic's "public" info.
export function theCard(fn, imageUrl, imageMimeType, note) {
  let card = null;
  fn = fn && fn.trim();
  note = note && note.trim();

  if (fn) {
    card = {
      fn: fn
    };
  }

  if (typeof note == 'string') {
    card = card || {};
    card.note = note ? note : Tinode.DEL_CHAR;
  }

  if (imageUrl) {
    card = card || {};
    let mimeType = imageMimeType;
    // Is this a data URL "data:[<mediatype>][;base64],<data>"?
    const matches = /^data:(image\/[-a-z0-9+.]+)?(;base64)?,/i.exec(imageUrl);
    if (matches) {
      mimeType = matches[1];
      card.photo = {
        data: imageUrl.substring(imageUrl.indexOf(',') + 1),
        ref: Tinode.DEL_CHAR
      };
    } else {
      card.photo = {
        data: Tinode.DEL_CHAR,
        ref: imageUrl
      };
    }
    card.photo.type = (mimeType || 'image/jpeg').substring('image/'.length);
  }

  return card;
}

// Deep-shallow compare two arrays: shallow compare each element.
export function arrayEqual(a, b) {
  if (a === b) {
    return true;
  }

  if (!Array.isArray(a) || !Array.isArray(b)) {
    return false;
  }

  // Compare lengths first.
  if (a.length != b.length) {
    return false;
  }
  // Order of elements is ignored.
  a.sort();
  b.sort();
  for (let i = 0, l = a.length; i < l; i++) {
    if (a[i] !== b[i]) {
      return false;
    }
  }
  return true;
}

// Checks (loosely) if the given string is a phone. If so, returns the phone number in a format
// as close to E.164 as possible.
export function asPhone(val) {
  val = val.trim();
  if (/^(?:\+?(\d{1,3}))?[- (.]*(\d{3})[- ).]*(\d{3})[- .]*(\d{2})[- .]*(\d{2})?$/.test(val)) {
    return val.replace(/[- ().]*/, '');
  }
  return null;
}

// Checks (loosely) if the given string is an email. If so returns the email.
export function asEmail(val) {
  val = val.trim();
  if (/^[a-zA-Z0-9_.+-]+@[a-zA-Z0-9-]+\.[a-zA-Z0-9-.]+$/.test(val)) {
    return val;
  }
  return null;
}

// Checks if URL is a relative url, i.e. has no 'scheme://', including the case of missing scheme '//'.
// The scheme is expected to be RFC-compliant, e.g. [a-z][a-z0-9+.-]*
// example.html - ok
// https:example.com - not ok.
// http:/example.com - not ok.
// //example.com or \\example.com - not ok.
// ' ↲ https://example.com' - not ok. (↲ means carriage return)
export function isUrlRelative(url) {
  // Replacing backslashes with forward slashes to mimic JS URL parser then testing for
  // 'scheme:' and '//' with optional space at the start.
  return url && !/^\s*([a-z][a-z0-9+.-]*:|\/\/)/im.test(url.replace(/\\/g, '/'));
}

// Ensure URL does not present an XSS risk. Optional allowedSchemes may contain an array of
// strings with permitted URL schemes, such as ['ftp', 'ftps']; otherwise accept http and https only.
export function sanitizeUrl(url, allowedSchemes) {
  if (typeof url != 'string') {
    return url;
  }

  // Strip control characters and whitespace. They are not valid URL characters anyway.
  url = url.replace(/[^\x21-\x7E]/gmi, '').trim();

  // Replace backslashes with forward slashes. They will be replaced in the URL parser anyway.
  url = url.replace(/\\/g, '/');

  // Relative URLs are safe.
  // Relative URL does not start with ':', abcd123: or '//'.
  if (!/^([a-z][a-z0-9+.-]*:|\/\/)/i.test(url)) {
    return url;
  }

  // Blob URLs are safe.
  if (/^blob:http/.test(url)) {
    return url;
  }

  // Absolute URL. Accept only safe schemes, or no scheme.
  const schemes = Array.isArray(allowedSchemes) ? allowedSchemes.join('|') : 'http|https';
  const re = new RegExp('^((' + schemes + '):|//)', 'i');
  if (!re.test(url)) {
    return null;
  }

  return url;
}

// Ensure URL is suitable as a source like <img src="url"> field: the URL must be a relative URL or
// have http:, https:, blob: or data: scheme.
// In case of data: scheme, the URL must must be of the right MIME type such as 'data:{mimeMajor}/XXXX;base64,'.
export function sanitizeUrlForMime(url, mimeMajor) {
  if (!url) {
    return null;
  }

  const sanitizedUrl = sanitizeUrl(url);
  if (sanitizedUrl) {
    return sanitizedUrl;
  }

  // Is this a data: URL of the appropriate mime type?
  const re = new RegExp(`data:${mimeMajor}\/[a-z0-9.-]+;base64,`, 'i');
  if (re.test(url.trim())) {
    return url;
  }

  return null;
}

// Append query parameter 'asatt=1' to the URL.
// It will cause Tinode server to add 'Content-Disposition: attachment' header when serving it.
// The URL is a string. The URL here is always absolute.
export function urlAsAttachment(url) {
  // TODO: check if URL is local or remote, i.e. compare to window.location.origin.
  /*
  if (typeof window.location == 'object') {
    if (!url.startsWith(window.location.origin)) {
      return url;
    }
  }
  */
  // Check if the URL is a data or blob. Do not alter such URLs.
  if (url.startsWith('data:') || url.startsWith('blob:')) {
    return url;
  }

  // Split URL into host+path, query, fragment.
  let query = '', fragment = '';
  const idxF = url.indexOf('#');
  if (idxF > 0) {
    fragment = url.substring(idxF+1);
    url = url.substring(0, idxF);
  }
  const idxQ = url.indexOf('?');
  if (idxQ > 0) {
    query = url.substring(idxQ+1);
    url = url.substring(0, idxQ);
  }
  // Add parameter and reassemble.
  const params = new URLSearchParams(query);
  params.append('asatt', '1');
  return `${url}?${params.toString()}` + (fragment ? `#${fragment}` : '');
}

/**
 * For outgoing messages, msgStatus() may be temporarily lower after reload because peer
 * read/recv info in subscriber state is not restored from cache. Until meta.sub arrives,
 * combine the live status with persisted msg._status and keep the higher value in the
 * SENT..READ range.
 */
export function isDeliveryTickStatus(status) {
  return status >= Tinode.MESSAGE_STATUS_SENT && status <= Tinode.MESSAGE_STATUS_READ;
}

/**
 * Derive delivery status from peer subscribers' read/recv seq (not from me-topic
 * contact.read/recv, which track the operator's own cursor).
 */
function deliveryStatusFromPeerReceipts(topic, seq) {
  const tinode = topic._tinode;
  const me = tinode?.getCurrentUserID();
  if (!me || !(seq > 0)) {
    return null;
  }

  let peerRead = 0;
  let peerRecv = 0;
  const consider = (sub) => {
    if (!sub?.user || sub.user === me) {
      return;
    }
    peerRead = Math.max(peerRead, sub.read | 0);
    peerRecv = Math.max(peerRecv, sub.recv | 0);
  };

  topic.subscribers(consider);
  if (topic.isP2PType?.()) {
    consider(topic.p2pPeerDesc());
  }

  if (peerRead >= seq) {
    return Tinode.MESSAGE_STATUS_READ;
  }
  if (peerRecv >= seq) {
    return Tinode.MESSAGE_STATUS_RECEIVED;
  }
  return null;
}

/**
 * After meta.sub arrives, recompute and persist outgoing message statuses. Needed on
 * fresh load when {data} may have been processed before peer read/recv was available.
 *
 * Only ever raises a status: this also runs while peer read/recv is still empty (right after
 * a reload or re-subscribe), where recomputing would overwrite cached read ticks with "sent".
 */
export function refreshOutgoingMessageStatuses(topic) {
  const tinode = topic._tinode;
  if (!tinode) {
    return;
  }
  topic.messages(msg => {
    if (!tinode.isMe(msg.from) || (msg.seq | 0) <= 0) {
      return;
    }
    const persisted = msg._status;
    if (isDeliveryTickStatus(persisted) && topic.msgStatus(msg, false) <= persisted) {
      return;
    }
    topic.msgStatus(msg, true);
  });
}

export function resolvedOutgoingDeliveryStatus(topic, msg) {
  let computed = topic.msgStatus(msg, false);
  const tinode = topic._tinode;
  if (!msg || !tinode?.isMe(msg.from)) {
    return computed;
  }

  const seq = msg.seq | 0;
  if (seq > 0 && isDeliveryTickStatus(computed)) {
    const fromPeer = deliveryStatusFromPeerReceipts(topic, seq);
    if (fromPeer != null) {
      computed = Math.max(computed, fromPeer);
    }
  }

  const persisted = msg._status;
  if (persisted == null) {
    return computed;
  }
  if (!isDeliveryTickStatus(computed) || !isDeliveryTickStatus(persisted)) {
    return computed;
  }
  return Math.max(computed, persisted);
}

// Given message's received status, return name and color of a delivery indicator icon.
export function deliveryMarker(received) {
  switch (received) {
    case Tinode.MESSAGE_STATUS_SENDING:
      // Icon sits on thread background (not the blue bubble); must not use --right-text-color.
      return { name: 'access_time', color: 'delivery-timestamp' }; // watch face
    case Tinode.MESSAGE_STATUS_FAILED:
    case Tinode.MESSAGE_STATUS_FATAL:
      return { name: 'error', color: 'danger-color' }; // red icon /!\
    case Tinode.MESSAGE_STATUS_SENT:
      return { name: 'done', color: 'delivery-timestamp' }; // checkmark
    case Tinode.MESSAGE_STATUS_RECEIVED:
      return { name: 'done_all', color: 'delivery-timestamp' }; // double checkmark
    case Tinode.MESSAGE_STATUS_READ:
      return { name: 'done_all', color: 'blue' }; // blue double checkmark
  }
  return null;
}

// Wraps a promise to make it cancelable.
// The parameter can be either a promise or an error. If it's an error, the wrapped promise is
// created in a rejected state.
export function cancelablePromise(promise) {
  let hasCanceled = false;

  const wrappedPromise = promise instanceof Error ?
    Promise.reject(promise) :
    new Promise((resolve, reject) => {
      promise.then(
        result => hasCanceled ? reject({isCanceled: true}) : resolve(result),
        error => hasCanceled ? reject({isCanceled: true}) : reject(error)
      );
    });

  return {
    promise: wrappedPromise,
    cancel() {
      hasCanceled = true;
    },
  };
};

// Clips string to the specified length.
export function clipStr(str, length) {
  return str && str.substring(0, length);
}
